import React, { useState, useEffect } from 'react';
import { getCollection, deleteDocument, updateDocument } from '../services/resilientDb';
import { supabase } from '../supabase';
import './WorkersPage.css';

const WorkersPage = () => {
    const [workers, setWorkers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterService, setFilterService] = useState('all');

    useEffect(() => {
        fetchWorkers();
    }, []);

    const fetchWorkers = async () => {
        try {
            // Resilient fetch: tries Firebase first, falls back to Supabase
            const workersList = await getCollection('serviceProviders');
            setWorkers(workersList);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching workers:', error);
            setLoading(false);
        }
    };

    const handleDelete = async (workerId) => {
        if (window.confirm('Delete this worker?\n\nThis will permanently remove their profile AND their login account so the email can be re-used.')) {
            try {
                // Delete Firebase Auth account via admin edge function (workerId === Firebase UID)
                try {
                    await supabase.functions.invoke('firebase-admin', {
                        body: { action: 'deleteUser', uid: workerId },
                    });
                } catch (authErr) {
                    console.warn('Firebase Auth user could not be deleted automatically:', authErr);
                }
                await deleteDocument('serviceProviders', workerId);
                setWorkers(workers.filter(worker => worker.id !== workerId));
                alert('Worker deleted successfully. Their login account has been removed and the email can be re-used.');
            } catch (error) {
                console.error('Error deleting worker:', error);
                alert('Failed to delete worker');
            }
        }
    };

    const handleToggleActive = async (workerId, currentStatus) => {
        try {
            const newStatus = !currentStatus;
            await updateDocument('serviceProviders', workerId, { isActive: newStatus });
            setWorkers(workers.map(worker =>
                worker.id === workerId ? { ...worker, isActive: newStatus } : worker
            ));
            alert(`Worker ${newStatus ? 'activated' : 'deactivated'} successfully`);
        } catch (error) {
            console.error('Error updating worker status:', error);
            alert('Failed to update worker status');
        }
    };

    const filteredWorkers = workers.filter(worker => {
        const matchesSearch = worker.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            worker.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            worker.service?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterService === 'all' || worker.service === filterService;
        return matchesSearch && matchesFilter;
    });

    const services = [...new Set(workers.map(w => w.service).filter(Boolean))];

    if (loading) {
        return (
            <div className="workers-page">
                <div className="container">
                    <div className="loading">Loading workers...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="workers-page">
            <div className="container">
                <div className="page-header">
                    <div>
                        <h1>Service Professionals</h1>
                        <p>Manage all registered service providers</p>
                    </div>
                    <a href="/add-worker" className="btn btn-primary">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Add Worker
                    </a>
                </div>

                <div className="filters-section">
                    <div className="search-box">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Search workers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <select
                        value={filterService}
                        onChange={(e) => setFilterService(e.target.value)}
                        className="service-filter"
                    >
                        <option value="all">All Services</option>
                        {services.map(service => (
                            <option key={service} value={service}>{service}</option>
                        ))}
                    </select>
                </div>

                {filteredWorkers.length === 0 ? (
                    <div className="empty-state">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                        <h3>No workers found</h3>
                        <p>No workers match your search criteria.</p>
                    </div>
                ) : (
                    <div className="workers-grid">
                        {filteredWorkers.map((worker) => (
                            <div key={worker.id} className="worker-card">
                                <div className="worker-header">
                                    <div className="worker-avatar">
                                        {worker.photoURL ? (
                                            <img src={worker.photoURL} alt={worker.name} />
                                        ) : (
                                            <span>{worker.name?.charAt(0).toUpperCase() || 'W'}</span>
                                        )}
                                    </div>
                                    <div className={`worker-status ${worker.isActive ? 'active' : 'inactive'}`}>
                                        {worker.isActive ? 'Active' : 'Inactive'}
                                    </div>
                                </div>

                                <div className="worker-info">
                                    <h3>{worker.name || 'Unknown Worker'}</h3>
                                    <p className="worker-service">{worker.service || 'No service specified'}</p>

                                    <div className="worker-details">
                                        <div className="detail-row">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                                <polyline points="22,6 12,13 2,6" />
                                            </svg>
                                            <span>{worker.email || 'No email'}</span>
                                        </div>

                                        <div className="detail-row">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                            </svg>
                                            <span>{worker.phone || 'No phone'}</span>
                                        </div>

                                        {worker.experience && (
                                            <div className="detail-row">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                                                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                                                </svg>
                                                <span>{worker.experience} years experience</span>
                                            </div>
                                        )}

                                        {worker.rating && (
                                            <div className="detail-row">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="#FFD700" stroke="#FFD700">
                                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                                </svg>
                                                <span>{worker.rating} / 5</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="worker-actions">
                                    <button
                                        onClick={() => handleToggleActive(worker.id, worker.isActive)}
                                        className={`toggle-btn ${worker.isActive ? 'deactivate' : 'activate'}`}
                                    >
                                        {worker.isActive ? 'Deactivate' : 'Activate'}
                                    </button>
                                    <button
                                        onClick={() => handleDelete(worker.id)}
                                        className="delete-btn"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <polyline points="3 6 5 6 21 6" />
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        </svg>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default WorkersPage;
